'use client';

import { useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FilterPill from './FilterPill';
import { FilterInput, LogicalOperator, OperatorOption } from '../../constants';

interface Props {
  groupIndex: number;
  filters: FilterInput[];
  logicalOperator: LogicalOperator;
  showDelete: boolean;
  onUpdateFilter: (
    groupIndex: number,
    filterIndex: number,
    patch: Partial<FilterInput>,
  ) => void;
  onRemoveFilter: (groupIndex: number, filterIndex: number) => void;
  onAddFilter: (groupIndex: number, filter: FilterInput) => void;
  onUpdateOperator: (groupIndex: number, op: LogicalOperator) => void;
  onRemoveGroup: (groupIndex: number) => void;
}

export default function FilterGroupContainer({
  groupIndex,
  filters,
  logicalOperator,
  showDelete,
  onUpdateFilter,
  onRemoveFilter,
  onAddFilter,
  onUpdateOperator,
  onRemoveGroup,
}: Props) {
  const handleUpdateFilter = useCallback(
    (filterIndex: number, patch: Partial<FilterInput>) => {
      onUpdateFilter(groupIndex, filterIndex, patch);
    },
    [groupIndex, onUpdateFilter],
  );

  const handleRemoveFilter = useCallback(
    (filterIndex: number) => {
      onRemoveFilter(groupIndex, filterIndex);
    },
    [groupIndex, onRemoveFilter],
  );

  const handleUpdateOperator = useCallback(
    (op: LogicalOperator) => {
      onUpdateOperator(groupIndex, op);
    },
    [groupIndex, onUpdateOperator],
  );

  return (
    <Box
      sx={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'grey.300',
        borderRadius: '12px',
        p: { xs: 1.5, sm: 2 },
        position: 'relative',
        backgroundColor: 'action.hover',
      }}
    >
      {showDelete && (
        <IconButton
          size="small"
          onClick={() => onRemoveGroup(groupIndex)}
          sx={(theme) => ({
            position: 'absolute',
            top: -14,
            right: -14,
            width: '28px',
            height: '28px',
            backgroundColor: 'background.paper',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: `${(theme.vars || theme).palette.error.light}18`,
              borderColor: 'error.main',
              color: 'error.main',
            },
          })}
        >
          <DeleteOutlineIcon sx={{ fontSize: '16px' }} />
        </IconButton>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {filters.length > 1 && (
            <Box
              component="button"
              onClick={() =>
                handleUpdateOperator(
                  logicalOperator === LogicalOperator.and
                    ? LogicalOperator.or
                    : LogicalOperator.and,
                )
              }
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                height: '28px',
                px: 1.5,
                fontSize: '12px',
                fontWeight: 600,
                color: 'primary.main',
                backgroundColor: 'transparent',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'primary.main',
                borderRadius: '14px',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: `${(theme.vars || theme).palette.primary.light}20`,
                },
              })}
            >
              {logicalOperator === LogicalOperator.and
                ? LogicalOperator.and
                : LogicalOperator.or}
            </Box>
          )}

          {filters.map((input, filterIndex) => (
            <FilterPill
              key={filterIndex}
              field={input.field}
              index={filterIndex}
              operator={input.operator}
              value={input.value}
              onUpdateFilter={handleUpdateFilter}
              onRemoveFilter={handleRemoveFilter}
              onUpdateOperator={handleUpdateOperator}
              groupOperator={logicalOperator}
            />
          ))}
        </Box>

        <Box
          component="button"
          onClick={() =>
            onAddFilter(groupIndex, {
              field: '',
              operator: OperatorOption.contains.value,
              value: '',
            })
          }
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: '34px',
            px: 1.5,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: 'grey.400',
            borderRadius: '20px',
            backgroundColor: 'transparent',
            color: 'info.main',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': {
              borderColor: 'info.main',
              backgroundColor: `${(theme.vars || theme).palette.info.main}08`,
            },
          })}
        >
          <AddIcon sx={{ fontSize: '16px' }} />
          Add filter
        </Box>
      </Box>
    </Box>
  );
}
